import { 
  db, 
  auth, 
  doc,
  getDocs,
  getDoc,
  updatePassword,
  collection,
  collectionGroup,
  where,
  limit,
  startAfter,
  orderBy,
  startAt,
  endAt,
  query,
} from "../../firebase";
import UserRepository from "../usecases/ports/userRepository";
import { User, UserType, TotalUsersType } from "../entities/user";
import { capitalizeFirstChar } from "../../utils/helpers/strings";
import axios from "../../axios";
import { data as constants } from "../../constants";
import { Query as QueryEntity } from "../entities/query";

export const USER_COLLECTION = "user";
export default class UserFirebaseImpl implements UserRepository {
  fetchUser = async (uid: string, isAdmin: boolean): Promise<User> => {
    try {
      // const type = isAdmin ? "admin" : "user";
      const docRef = doc(db, USER_COLLECTION, uid);
      const docSnap = await getDoc(docRef);
      if (!navigator.onLine && docSnap.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }
      const userInfo = docSnap.data();

      return userInfo as User;
    } catch (error) {
      throw error;
    }
  };

  changePassword = async (newPassword: string): Promise<void> => {
    try {
      const user = auth.currentUser;
      if (user) {
        if (!navigator.onLine) {
          throw new Error(constants.commonNetworkError);
        }
        await updatePassword(user, newPassword);
      }
    } catch (error) {
      throw error;
    }
  };

  addUser = async (params: User) => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      const { name: fullName, ...others  } = params;
      const userParams = { fullName, ...others  };
      await axios.post(`/user/add`, userParams);
    } catch (error) {
      throw error;
    }
  };

  fetchUsers = async (
    queryEntity: QueryEntity,
  ): Promise<{
    data?: UserType[];
    lastElement?: unknown;
    firstElement?: unknown;
  }> => {
    try {
      const { where: whereQuery, orderBy: orderByQuery, limit: limitQuery, startAfter: startAfterQuery } = queryEntity;
      const collectionRef = collectionGroup(db, USER_COLLECTION);
      const queryConstraints = [];
      const transformedData: any[] = [];

      if (whereQuery && whereQuery?.length > 0) {
        whereQuery.forEach((whereItem) => {
          if (typeof whereItem[0] !== "undefined" &&
            typeof whereItem[1] !== "undefined" &&
            typeof whereItem[2] !== "undefined") {
            queryConstraints.push(where(whereItem[0], whereItem[1], whereItem[2]));
          }
        });
      }
      if (orderByQuery && orderByQuery?.length > 0) {
        orderByQuery.forEach((orderByParams) => {
          const [key, value] = orderByParams;
          queryConstraints.push(orderBy(key, value));
        });
      }
      if (limitQuery) {
        queryConstraints.push(limit(limitQuery));
      }
      if (startAfterQuery) {
        queryConstraints.push(startAfter(startAfterQuery));
      }

      const q = query(collectionRef, ...queryConstraints);
      const docData = await getDocs(q);

      if (docData.empty && docData.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      docData.forEach((data) => {
        const _data = data.data();
        transformedData.push({
          id: data.id,
          key: data.id,
          fullName: _data.name,
          accessType: capitalizeFirstChar(_data?.accessType || ''),
          email: _data.email,
          status: capitalizeFirstChar(_data?.accountStatus || ''),
          createdAt: _data?.createdAt,
        });
      });

      return {
        data: transformedData,
        lastElement: docData?.docs[docData?.docs.length - 1],
        firstElement: docData?.docs[0],
      };
    } catch (error) {
      throw error;
    }
  };

  fetchUserById = async (uid: string): Promise<User> => {
    try {
      const userDocRef = doc(db, USER_COLLECTION, uid);
      const userDocSnap = await getDoc(userDocRef);
      let userInfo = undefined;

      if (userDocSnap.exists()) {
        userInfo = userDocSnap.data();
      }

      return userInfo as any;
    } catch (error) {
      throw error;
    }
  };

  updateUser = async (uid: string, data: Partial<User>) => {
    try {
      if (!navigator.onLine) {
        throw new Error(constants.commonNetworkError);
      }
      await axios.put(`/user/update/${uid}`, { ...data });
    } catch (error) {
      throw error;
    }
  };

  searchUsers = async (keyword: string) => {
    try {
      const userRef = collection(db, USER_COLLECTION);
      const term = keyword?.toLowerCase();
      const reverseTerm = term.split("").reverse().join("")?.toLowerCase();

      const labelQuery = query(userRef, orderBy("lowerCaseName"), startAt(term), endAt(term + "~"));
      const reverseLabelQuery = query(userRef, orderBy("lowerCaseName"), startAt(reverseTerm), endAt(reverseTerm + "~"));

      const [labelSnap, reverseLabelSnap] = await Promise.all([
        getDocs(labelQuery),
        getDocs(reverseLabelQuery),
      ]);

      const labelPretty = labelSnap.docs.map((user) => ({ ...user.data() }));
      const labelReversePretty = reverseLabelSnap.docs.map((user) => ({
        ...user.data(),
      }));

      const concatenatedData = labelPretty.concat(labelReversePretty);

      const data = concatenatedData.reduce((arr: any, user: any) => {
        if (!arr.some((obj: any) => obj.id === user.id)) {
          arr.push(user);
        }

        return arr;
      }, []);

      return data;
    } catch (error) {
      throw error;
    }
  };

  fetchTotalUsers = async (): Promise<TotalUsersType> => {
    try {
      let totalObject = {};
      const totalUsersRef = collection(db, "totalUsers");
      const totalUsersDocData = await getDocs(totalUsersRef);

      if (totalUsersDocData.empty && totalUsersDocData.metadata.fromCache) {
        throw new Error(constants.commonNetworkError);
      }

      totalUsersDocData.forEach((data) => {
        const _data = data.data();
        totalObject = {
          ...totalObject,
          [data.id]: {
            ..._data,
          },
        };
      });

      return totalObject as TotalUsersType;
    } catch (error) {
      throw error;
    }
  };
}
